import { Injectable } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ChooseParentPortfolioComponent } from 'app/modules/properties/modals/choose-parent-portfolio/choose-parent-portfolio.component';
import { PropertyDealAddComponent } from 'app/modules/properties/modals/deal-add/deal-add.component';
import { map, Observable, take } from 'rxjs';

import { Project } from '../../projects.types';
import { HttpClient } from '@angular/common/http';
import { Dictionary } from 'tsui';

export interface PortfolioMerge {
  destination_portfolio_id?: number;
  destination_portfolio_name?: string;
  destination_portfolio_layout_id: number;
  destination_portfolio_pipeline_id: number;
  child_ids: number[];
  confirmed?: boolean;
}

export interface PortfolioMergePreviewData {
  portfolioName: string;
  children: string[];
  id?: number;
}

export interface PortfolioSeparate {
  project_ids: number[];
  confirmed?: boolean;
}

export interface SeparationPreviewData {
  id?: number;
  project_ids: number[];
  preview?: {
    projects: Array<{
      id: number;
      title: string;
    }>;
  };
}

@Injectable()
export class MergeProjectService {
  constructor(
    private readonly matDialog: MatDialog,
    private readonly httpClient: HttpClient,
  ) {}

  portfolioMerge(payload: PortfolioMerge): Observable<PortfolioMergePreviewData> {
    return this.httpClient.post(`portfolio_merges`, { portfolio_merge: payload }).pipe(
      map(({ portfolio_merge }: { portfolio_merge: { id?: number; preview: Dictionary<string[]> } }) => {
        const [firstKey] = Object.keys(portfolio_merge.preview);
        return {
          id: portfolio_merge.id,
          portfolioName: firstKey,
          children: portfolio_merge.preview[firstKey],
        };
      }),
    );
  }

  portfolioSeparate(payload: PortfolioSeparate): Observable<SeparationPreviewData> {
    return this.httpClient.post(`portfolio_separations`, { portfolio_separation: payload }).pipe(
      map(({ portfolio_separation }: { portfolio_separation: SeparationPreviewData }) => {
        return portfolio_separation;
      }),
    );
  }

  public merge(projects: Project[], opts: any): Observable<{ success: boolean }> {
    if (this.onlySingleProjects(projects)) {
      return this.openCreatePortfolioDialog(projects, opts);
    }

    if (this.onlyOneIsParent(projects)) {
      return this.addToPortfolio(projects);
    }

    if (this.moreThanOneIsParent(projects)) {
      return this.chooseParentDialog(projects);
    }
  }

  private openCreatePortfolioDialog(projects: Project[], data): Observable<{ success: boolean }> {
    const dialogRef = this.matDialog.open(PropertyDealAddComponent, {
      panelClass: 'mailbox-compose-dialog',
      width: '500px',
      disableClose: true,
      data: {
        data,
        selectedChildProjects: { selected: [] },
        child_ids: projects.map(({ id }) => id),
        dealType: 'mergeIntoPortfolio',
      },
    });

    return dialogRef.afterClosed().pipe(take(1));
  }

  private addToPortfolio(projects: Project[], parentProject?: Project): Observable<{ success: boolean }> {
    if (!parentProject) {
      parentProject = projects.find((project) => project.is_parent === 'true');
    }
    const childProjects = projects.filter((project) => parentProject.id !== project.id);

    const dialogRef = this.matDialog.open(ChooseParentPortfolioComponent, {
      panelClass: 'mailbox-compose-dialog',
      width: '500px',
      disableClose: true,
      data: {
        parentProject,
        childProjects,
      },
    });

    return dialogRef.afterClosed();
  }

  private chooseParentDialog(projects: Project[]): Observable<{ success: boolean }> {
    const dialogRef = this.matDialog.open(ChooseParentPortfolioComponent, {
      panelClass: 'mailbox-compose-dialog',
      width: '500px',
      disableClose: true,
      data: {
        projects,
      },
    });

    return dialogRef.afterClosed();
  }

  private onlySingleProjects(projects: Project[]): boolean {
    return projects.every((project) => {
      return project.is_parent === 'false';
    });
  }

  private onlyOneIsParent(projects: Project[]): boolean {
    return projects.filter((project) => project.is_parent === 'true').length === 1;
  }

  private moreThanOneIsParent(projects: Project[]): boolean {
    return projects.filter((project) => project.is_parent === 'true').length > 1;
  }
}
